import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import {
  collection, addDoc, serverTimestamp, setDoc, doc,
  query, orderBy, onSnapshot, where, getDocs, updateDoc,
} from 'firebase/firestore';
import axios from 'axios';
import { getAllArticles } from '../data/articles';

const API = process.env.REACT_APP_API_URL || 'https://clicksaver.app';
const CHAT_COL = 'chats';

const SUGGESTIONS = [
  'How do I install the tracking script?',
  'What is click fraud?',
  'How does auto IP blocking work?',
  'Connect to Google Ads',
];

// ── Knowledge search ──────────────────────────────────────────────────────────
function searchKB(rawQ) {
  const articles = getAllArticles();
  const q = rawQ.toLowerCase().trim();
  if (!q) return [];
  const words = q.split(/\s+/).filter(w => w.length > 2);
  if (!words.length) return [];
  return articles
    .map(a => {
      let score = 0;
      const haystack = [
        a.title, a.category || '', a.intro || '',
        ...(a.sections || []).map(s => `${s.heading || ''} ${s.content || ''}`),
      ].join(' ').toLowerCase();
      words.forEach(w => {
        const re = new RegExp(w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        score += (haystack.match(re) || []).length;
      });
      if (a.title.toLowerCase().includes(q)) score += 15;
      return { a, score };
    })
    .filter(r => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2)
    .map(r => r.a);
}

// ── Time formatter ─────────────────────────────────────────────────────────────
function fmt(ts) {
  if (!ts) return '';
  let d;
  if (typeof ts === 'string' || typeof ts === 'number') {
    d = new Date(ts);
  } else if (typeof ts.toDate === 'function') {
    d = ts.toDate();
  } else if (typeof ts.seconds === 'number') {
    d = new Date(ts.seconds * 1000);
  } else if (typeof ts._seconds === 'number') {
    d = new Date(ts._seconds * 1000);
  } else {
    return '';
  }
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  return d.toDateString() === now.toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ── Bot response builder ───────────────────────────────────────────────────────
function buildBotReply(text) {
  const articles = searchKB(text);
  const lower = text.toLowerCase();

  if (articles.length > 0) {
    return {
      text: articles.length === 1
        ? 'I found an article that should help:'
        : 'Here are some articles that might help:',
      articles,
      showEscalate: true,
    };
  }
  if (/price|cost|plan|billing|subscription|trial/.test(lower)) {
    return { text: 'You can view all our plans on the Pricing page.', link: { to: '/pricing', label: 'View Pricing →' }, showEscalate: true };
  }
  if (/install|setup|start|begin|add script|snippet/.test(lower)) {
    return { text: 'Getting started is quick — check the setup guides in the Knowledge Center.', link: { to: '/knowledge-center', label: 'Knowledge Center →' }, showEscalate: true };
  }
  if (/contact|email|support|human|person|agent|talk/.test(lower)) {
    return { text: 'Sure, I can connect you with our support team!', showEscalate: true, escalateNow: true };
  }
  return {
    text: "I couldn't find a specific article for that. Would you like to talk to a support team member?",
    showEscalate: true,
    escalateNow: false,
  };
}

// ── Main widget ───────────────────────────────────────────────────────────────
export default function AIChatWidget() {
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(auth.currentUser);
  // mode: 'ai' | 'live' | 'guest-form'
  const [mode, setMode] = useState('ai');
  const [aiMessages, setAiMessages] = useState([]);
  const [thinking, setThinking] = useState(false);
  const [input, setInput] = useState('');
  // Live chat (logged-in)
  const [liveMessages, setLiveMessages] = useState([]);
  const [liveInput, setLiveInput] = useState('');
  const [liveSending, setLiveSending] = useState(false);
  const [unread, setUnread] = useState(0);
  // Guest form
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestMessage, setGuestMessage] = useState('');
  const [guestPending, setGuestPending] = useState('');
  const [guestBusy, setGuestBusy] = useState(false);
  // Guest live chat (polling)
  const [guestId, setGuestId] = useState(() => localStorage.getItem('cs_guest_id') || '');
  const [guestLiveMessages, setGuestLiveMessages] = useState([]);
  const [guestLiveInput, setGuestLiveInput] = useState('');
  const [guestLiveSending, setGuestLiveSending] = useState(false);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const chatRef = useRef(null);

  // Init greeting
  useEffect(() => {
    setAiMessages([{
      id: 'greeting', role: 'bot',
      text: "Hi! I'm your ClickSaver AI assistant.\nHow can I help you today?",
      suggestions: SUGGESTIONS,
    }]);
  }, []);

  // Auto-restore guest conversation on mount — if this browser has an existing
  // guest_id with messages, drop the user straight into the live view so they
  // can continue the thread across tabs/reloads.
  useEffect(() => {
    if (user) return; // logged-in users have their own Firestore listener
    const id = localStorage.getItem('cs_guest_id');
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/chat/guest-messages?guest_id=${encodeURIComponent(id)}`);
        if (cancelled) return;
        if (Array.isArray(res.data) && res.data.length > 0) {
          setGuestLiveMessages(res.data);
          setMode('guest-live');
        }
      } catch (_) {}
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Auth
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => setUser(u));
    return unsub;
  }, []);

  // Scroll to bottom whenever any message list grows
  useEffect(() => {
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);
  }, [aiMessages, liveMessages, guestLiveMessages, thinking, mode]);

  // Focus input on open
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open, mode]);

  // Guest live polling — fetch messages for this guest id every 4s while in guest-live mode
  useEffect(() => {
    if (mode !== 'guest-live' || !guestId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/api/chat/guest-messages?guest_id=${encodeURIComponent(guestId)}`);
        if (!cancelled && Array.isArray(res.data)) setGuestLiveMessages(res.data);
      } catch (_) {}
    };
    load();
    const id = setInterval(load, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [mode, guestId]);

  // Live Firestore listener
  useEffect(() => {
    if (mode !== 'live' || !user) return;
    const ref = collection(db, CHAT_COL, user.uid, 'messages');
    const q = query(ref, orderBy('created_at', 'asc'));
    const unsub = onSnapshot(q, snap => {
      const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setLiveMessages(msgs);
      if (!open) setUnread(msgs.filter(m => m.sender === 'admin' && !m.read).length);
    });
    return unsub;
  }, [mode, user, open]);

  // Mark read
  useEffect(() => {
    if (!open || !user || mode !== 'live') return;
    const run = async () => {
      const ref = collection(db, CHAT_COL, user.uid, 'messages');
      const q = query(ref, where('sender', '==', 'admin'), where('read', '==', false));
      const snap = await getDocs(q);
      snap.docs.forEach(d => updateDoc(doc(db, CHAT_COL, user.uid, 'messages', d.id), { read: true }));
      setUnread(0);
    };
    run();
  }, [open, user, mode, liveMessages.length]);

  // ── AI send ──
  const handleAISend = useCallback(async (text) => {
    const t = (text || input).trim();
    if (!t || thinking) return;
    setInput('');
    setAiMessages(prev => [...prev, { id: Date.now(), role: 'user', text: t }]);
    setThinking(true);
    await new Promise(r => setTimeout(r, 700 + Math.random() * 400));
    const reply = buildBotReply(t);
    setAiMessages(prev => [...prev, { id: Date.now() + 1, role: 'bot', ...reply }]);
    setThinking(false);
    if (reply.escalateNow) {
      setTimeout(() => handleEscalate(t), 600);
    }
  }, [input, thinking]);

  // ── Escalate ──
  const handleEscalate = useCallback((msg = '') => {
    if (user) {
      setMode('live');
    } else {
      setGuestPending(msg);
      setGuestMessage('');
      setMode('guest-form');
    }
  }, [user]);

  // ── Guest form submit ──
  const handleGuestSubmit = async (e) => {
    e.preventDefault();
    if (!guestName.trim() || !guestEmail.trim() || !guestMessage.trim()) return;
    setGuestBusy(true);
    try {
      // Keep the same guest_id across submissions in the same browser — same
      // visitor = same conversation. Admin sees one thread, not many.
      const existingId = localStorage.getItem('cs_guest_id') || '';
      const res = await axios.post(`${API}/api/chat/guest-contact`, {
        name: guestName.trim(),
        email: guestEmail.trim(),
        message: guestMessage.trim(),
        guest_id: existingId || undefined,
      });
      const newId = res?.data?.guest_id || existingId;
      if (newId) {
        localStorage.setItem('cs_guest_id', newId);
        setGuestId(newId);
      }
      // Seed the live view with the submitted message; polling will replace it
      setGuestLiveMessages(prev => [...(prev || []), { id: `first-${Date.now()}`, text: guestMessage.trim(), sender: 'user', sender_name: guestName.trim(), created_at: new Date().toISOString() }]);
      setMode('guest-live');
      setGuestPending('');
    } catch {
      setAiMessages(prev => [...prev, {
        id: Date.now(), role: 'bot',
        text: "Something went wrong. Please email us at support@clicksaver.app",
      }]);
      setMode('ai');
    }
    setGuestBusy(false);
  };

  // ── Live send ──
  const handleLiveSend = async () => {
    if (!liveInput.trim() || !user || liveSending) return;
    const text = liveInput.trim();
    setLiveInput('');
    setLiveSending(true);
    try {
      await addDoc(collection(db, CHAT_COL, user.uid, 'messages'), {
        text, sender: 'user',
        sender_name: user.displayName || user.email || 'User',
        sender_email: user.email,
        created_at: serverTimestamp(), read: false,
      });
      await setDoc(doc(db, CHAT_COL, user.uid), {
        last_message: text, last_message_at: serverTimestamp(),
        user_email: user.email, user_name: user.displayName || user.email,
        has_unread_admin: true,
      }, { merge: true });
      const token = await user.getIdToken();
      axios.post(`${API}/api/chat/notify`, { text }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    } catch (err) { console.error(err); }
    setLiveSending(false);
  };

  // Guest live send (polling-based follow-up)
  const handleGuestLiveSend = async (text) => {
    const t = (text || guestLiveInput).trim();
    if (!t || guestLiveSending || !guestId) return;
    setGuestLiveInput('');
    setGuestLiveSending(true);
    const optimistic = { id: `o-${Date.now()}`, text: t, sender: 'user', sender_name: guestName.trim() || 'You', created_at: new Date().toISOString() };
    setGuestLiveMessages(prev => [...prev, optimistic]);
    try {
      await axios.post(`${API}/api/chat/guest-followup`, { guest_id: guestId, text: t });
    } catch (err) { console.error(err); }
    setGuestLiveSending(false);
  };

  // ── Render helpers ──────────────────────────────────────────────────────────

  const ArticleCard = ({ article }) => (
    <Link
      to={`/knowledge-center/${article.slug}`}
      onClick={() => setOpen(false)}
      className="cs-article-card"
      style={{ display: 'block', marginTop: 8, padding: '10px 14px', borderRadius: 10, transition: 'all 0.15s' }}
    >
      <div className="cs-article-title">{article.title}</div>
      <div className="cs-article-meta">{article.category} · {article.time} read →</div>
    </Link>
  );

  const EscalateBtn = ({ msg }) => (
    <button
      onClick={() => handleEscalate(msg)}
      className="cs-escalate-btn"
      style={{ marginTop: 10, fontSize: 12, fontWeight: 600, borderRadius: 20, padding: '5px 14px', cursor: 'pointer', transition: 'background 0.15s' }}
    >
      💬 Talk to a human →
    </button>
  );

  // ── Chat window content ──────────────────────────────────────────────────────

  const renderAIMode = () => (
    <>
      <div ref={chatRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {aiMessages.map(msg => (
          <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            {msg.role === 'bot' && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: '90%' }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, marginTop: 2 }}>CS</div>
                <div style={{ flex: 1 }}>
                  <div className="cs-msg-bot" style={{ borderRadius: '0 12px 12px 12px', padding: '10px 14px', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-line' }}>
                    {msg.text}
                  </div>
                  {msg.articles?.map((a, i) => <ArticleCard key={i} article={a} />)}
                  {msg.link && (
                    <Link to={msg.link.to} onClick={() => setOpen(false)} style={{ display: 'inline-block', marginTop: 8, fontSize: 13, fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}>
                      {msg.link.label}
                    </Link>
                  )}
                  {msg.showEscalate && <EscalateBtn msg={msg.text} />}
                  {msg.suggestions && (
                    <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {msg.suggestions.map((s, i) => (
                        <button key={i} onClick={() => handleAISend(s)} className="cs-chip"
                          style={{ fontSize: 12, padding: '5px 12px', borderRadius: 20, cursor: 'pointer', transition: 'all 0.15s' }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {msg.role === 'user' && (
              <div className="cs-msg-user" style={{ maxWidth: '80%', borderRadius: '12px 12px 4px 12px', padding: '10px 14px', fontSize: 13, lineHeight: 1.6 }}>
                {msg.text}
              </div>
            )}
          </div>
        ))}
        {thinking && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700 }}>CS</div>
            <div className="cs-msg-bot" style={{ borderRadius: '0 12px 12px 12px', padding: '10px 16px' }}>
              <span className="cs-typing"><span/><span/><span/></span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>
      {/* Input */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleAISend()}
            placeholder="Ask me anything..."
            style={{ flex: 1, borderRadius: 24, padding: '10px 14px', width: 'auto' }}
          />
          <button
            onClick={() => handleAISend()}
            disabled={!input.trim() || thinking}
            className={input.trim() ? 'cs-send-btn-active' : 'cs-send-btn-inactive'}
            style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s' }}
          >➤</button>
        </div>
      </div>
    </>
  );

  const renderLiveMode = () => (
    <>
      {/* Back to AI */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={() => setMode('ai')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#64748b', fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          ← Back to AI Assistant
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>Live support</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {liveMessages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Connected to support</div>
            <div style={{ fontSize: 12 }}>Send a message and our team will reply shortly.</div>
          </div>
        ) : liveMessages.map(msg => {
          const isUser = msg.sender === 'user';
          return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', maxWidth: '85%', alignSelf: isUser ? 'flex-end' : 'flex-start' }}>
              <div className={isUser ? 'cs-live-msg-user' : 'cs-live-msg-admin'} style={{ padding: '10px 14px', borderRadius: 14, borderBottomRightRadius: isUser ? 4 : 14, borderBottomLeftRadius: isUser ? 14 : 4, fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}>
                {msg.text}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{fmt(msg.created_at)}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={inputRef}
            value={liveInput}
            onChange={e => setLiveInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleLiveSend()}
            placeholder="Type a message..."
            style={{ flex: 1, borderRadius: 24, padding: '10px 14px', width: 'auto' }}
          />
          <button
            onClick={handleLiveSend}
            disabled={!liveInput.trim() || liveSending}
            className={liveInput.trim() ? 'cs-send-btn-active' : 'cs-send-btn-inactive'}
            style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >➤</button>
        </div>
      </div>
    </>
  );

  const renderGuestLiveMode = () => (
    <>
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 6 }}>
        <button onClick={() => setMode('ai')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#64748b', fontFamily: 'inherit', padding: 0, display: 'flex', alignItems: 'center', gap: 4 }}>
          ← Back to AI Assistant
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>Connected to support · replies appear here</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {guestLiveMessages.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
            <div style={{ fontSize: 28, marginBottom: 8 }}>👋</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Waiting for support</div>
            <div style={{ fontSize: 12 }}>Our team will reply here shortly.</div>
          </div>
        ) : guestLiveMessages.map(msg => {
          const isUser = msg.sender === 'user';
          return (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', maxWidth: '85%', alignSelf: isUser ? 'flex-end' : 'flex-start' }}>
              <div className={isUser ? 'cs-live-msg-user' : 'cs-live-msg-admin'} style={{ padding: '10px 14px', borderRadius: 14, borderBottomRightRadius: isUser ? 4 : 14, borderBottomLeftRadius: isUser ? 14 : 4, fontSize: 13, lineHeight: 1.5, wordBreak: 'break-word' }}>
                {msg.text}
              </div>
              <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 3 }}>{fmt(msg.created_at)}</div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <div style={{ padding: '10px 12px', borderTop: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            ref={inputRef}
            value={guestLiveInput}
            onChange={e => setGuestLiveInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleGuestLiveSend()}
            placeholder="Type a message..."
            style={{ flex: 1, borderRadius: 24, padding: '10px 14px', width: 'auto' }}
          />
          <button
            onClick={() => handleGuestLiveSend()}
            disabled={!guestLiveInput.trim() || guestLiveSending}
            className={guestLiveInput.trim() ? 'cs-send-btn-active' : 'cs-send-btn-inactive'}
            style={{ width: 38, height: 38, borderRadius: '50%', border: 'none', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}
          >➤</button>
        </div>
      </div>
    </>
  );

  const renderGuestForm = () => (
    <>
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center' }}>
        <button onClick={() => setMode('ai')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: '#64748b', fontFamily: 'inherit', padding: 0 }}>
          ← Back
        </button>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#94a3b8' }}>Contact support</span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 20 }}>
          <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#fff', fontWeight: 700, marginTop: 2 }}>CS</div>
          <div style={{ background: '#f1f5f9', borderRadius: '0 12px 12px 12px', padding: '10px 14px', fontSize: 13, color: '#0f172a', lineHeight: 1.6 }}>
            To connect you with our support team, please share your details.
          </div>
        </div>
        <form onSubmit={handleGuestSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <input
            required
            type="text"
            placeholder="Your name"
            value={guestName}
            onChange={e => setGuestName(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', color: '#0f172a' }}
          />
          <input
            required
            type="email"
            placeholder="Your email address"
            value={guestEmail}
            onChange={e => setGuestEmail(e.target.value)}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, outline: 'none', fontFamily: 'inherit', color: '#0f172a' }}
          />
          <textarea
            required
            placeholder="Your message"
            value={guestMessage}
            onChange={e => setGuestMessage(e.target.value)}
            rows={3}
            style={{ padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, background: '#f8fafc', color: '#0f172a', resize: 'none', fontFamily: 'inherit' }}
          />
          <button
            type="submit"
            disabled={guestBusy}
            style={{ padding: '11px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#2563eb,#7c3aed)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', marginTop: 4 }}
          >
            {guestBusy ? 'Sending…' : 'Send to Support'}
          </button>
          <div style={{ textAlign: 'center', fontSize: 11, color: '#94a3b8' }}>
            Or email us: <a href="mailto:support@clicksaver.app" style={{ color: '#2563eb', textDecoration: 'none' }}>support@clicksaver.app</a>
          </div>
        </form>
        <div ref={bottomRef} />
      </div>
    </>
  );

  // ── Full render ───────────────────────────────────────────────────────────────
  const totalUnread = unread;

  return (
    <>
      <style>{`
        @keyframes cs-bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-5px); }
        }
        @keyframes cs-slide-up {
          from { opacity: 0; transform: translateY(16px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1); }
        }
        /* Isolate widget from app dark-theme CSS variables */
        .cs-win { color: #0f172a !important; background-color: #fff !important; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important; }
        .cs-win * { box-sizing: border-box; }
        .cs-win a, .cs-win a:visited, .cs-win a:hover { color: inherit; text-decoration: none; }
        .cs-win input, .cs-win textarea {
          background-color: #f8fafc !important;
          color: #0f172a !important;
          border: 1.5px solid #e2e8f0 !important;
          font-family: inherit !important;
          font-size: 13px !important;
          width: 100% !important;
          outline: none !important;
          box-shadow: none !important;
        }
        .cs-win input:focus, .cs-win textarea:focus {
          border-color: #2563eb !important;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1) !important;
        }
        .cs-win button { font-family: inherit !important; }
        .cs-msg-bot { background-color: #f1f5f9 !important; color: #0f172a !important; }
        .cs-msg-user { background: linear-gradient(135deg,#2563eb,#7c3aed) !important; color: #fff !important; }
        .cs-article-card { background-color: #f8fafc !important; color: #0f172a !important; border: 1.5px solid #e2e8f0 !important; }
        .cs-article-card:hover { background-color: #eff6ff !important; border-color: #2563eb !important; }
        .cs-article-card .cs-article-title { color: #0f172a !important; font-size: 13px; font-weight: 600; margin-bottom: 2px; }
        .cs-article-card .cs-article-meta  { color: #64748b !important; font-size: 11px; }
        .cs-chip { background-color: #fff !important; color: #374151 !important; border: 1.5px solid #e2e8f0 !important; }
        .cs-chip:hover { background-color: #eff6ff !important; color: #2563eb !important; border-color: #2563eb !important; }
        .cs-escalate-btn { background: none !important; color: #2563eb !important; border: 1.5px solid #bfdbfe !important; }
        .cs-escalate-btn:hover { background-color: #eff6ff !important; }
        .cs-back-btn { background: none !important; border: none !important; color: #64748b !important; cursor: pointer; }
        .cs-live-msg-user { background: linear-gradient(135deg,#2563eb,#7c3aed) !important; color: #fff !important; }
        .cs-live-msg-admin { background-color: #f1f5f9 !important; color: #0f172a !important; }
        .cs-send-btn-active { background: linear-gradient(135deg,#2563eb,#7c3aed) !important; color: #fff !important; cursor: pointer; }
        .cs-send-btn-inactive { background-color: #e2e8f0 !important; color: #94a3b8 !important; cursor: default; }
        .cs-typing span {
          display: inline-block; width: 6px; height: 6px; border-radius: 50%;
          background-color: #94a3b8 !important; margin: 0 2px;
          animation: cs-bounce 1.2s ease-in-out infinite;
        }
        .cs-typing span:nth-child(2) { animation-delay: 0.2s; }
        .cs-typing span:nth-child(3) { animation-delay: 0.4s; }
      `}</style>

      {/* Bubble */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Chat with us"
        style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 10000,
          width: 56, height: 56, borderRadius: '50%', border: 'none',
          background: 'linear-gradient(135deg,#2563eb,#7c3aed)',
          color: '#fff', fontSize: 22, cursor: 'pointer',
          boxShadow: '0 4px 20px rgba(37,99,235,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'transform 0.2s, box-shadow 0.2s',
        }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 6px 28px rgba(37,99,235,0.55)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(37,99,235,0.45)'; }}
      >
        {open ? '✕' : '💬'}
        {!open && totalUnread > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -2, width: 20, height: 20, borderRadius: '50%', background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
            {totalUnread > 9 ? '9+' : totalUnread}
          </span>
        )}
      </button>

      {/* Chat window */}
      {open && (
        <div className="cs-win" style={{
          position: 'fixed', bottom: 92, right: 24, zIndex: 10000,
          width: 380, maxWidth: 'calc(100vw - 48px)',
          height: 520, maxHeight: 'calc(100vh - 140px)',
          borderRadius: 20,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18), 0 4px 16px rgba(0,0,0,0.08)',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          animation: 'cs-slide-up 0.2s ease',
        }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg,#2563eb,#7c3aed)', padding: '16px 18px', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, color: '#fff', fontWeight: 700, flexShrink: 0 }}>CS</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>ClickSaver Assistant</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ade80', display: 'inline-block' }} />
                  {mode === 'live' || mode === 'guest-live' ? 'Live support' : 'AI-powered · always online'}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 28, height: 28, color: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >✕</button>
            </div>
          </div>

          {/* Content */}
          {mode === 'ai'         && renderAIMode()}
          {mode === 'live'       && renderLiveMode()}
          {mode === 'guest-form' && renderGuestForm()}
          {mode === 'guest-live' && renderGuestLiveMode()}
        </div>
      )}
    </>
  );
}
